import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";

export interface ProfileSectionNavItem {
  anchorId: string;
  label: string;
  icon: LucideIcon;
  modified: boolean;
}

interface ProfileSectionNavProps {
  items: ProfileSectionNavItem[];
  profileCompleted: boolean;
}

function scrollToSection(anchorId: string) {
  document
    .getElementById(anchorId)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** Settings side nav (xl) / chip row (smaller screens) with scroll-spy and unsaved dots. */
export function ProfileSectionNav({
  items,
  profileCompleted,
}: ProfileSectionNavProps) {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const [activeId, setActiveId] = useState(items[0]?.anchorId ?? "");
  const anchorKey = items.map((item) => item.anchorId).join("|");

  useEffect(() => {
    const elements = anchorKey
      .split("|")
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element != null);
    if (elements.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [anchorKey]);

  const statusBadge = (
    <Badge
      variant={profileCompleted ? "success" : "warning"}
      size="sm"
      dot
    >
      {profileCompleted
        ? t("forms.profileStatus.completeTitle")
        : t("forms.profileStatus.incompleteTitle")}
    </Badge>
  );

  return (
    <nav aria-label={t("sectionsNav")}>
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] xl:hidden">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.anchorId}
              type="button"
              onClick={() => scrollToSection(item.anchorId)}
              className="border-surface-200 bg-surface-0 text-surface-700 hover:border-surface-300 hover:text-surface-900 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
            >
              <Icon
                className="text-surface-400 h-3.5 w-3.5"
                aria-hidden
              />
              {item.label}
              {item.modified ? (
                <span
                  className="bg-warning-500 h-1.5 w-1.5 rounded-full"
                  aria-hidden
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="hidden xl:block">
        <div className="mb-3 flex items-center justify-between gap-2 px-2.5">
          <p className="text-surface-500 text-xs font-medium">
            {t("sectionsNav")}
          </p>
          {statusBadge}
        </div>
        <ul className="space-y-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = item.anchorId === activeId;
            return (
              <li key={item.anchorId}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveId(item.anchorId);
                    scrollToSection(item.anchorId);
                  }}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                    isActive
                      ? "bg-surface-0 text-surface-900 ring-surface-200 shadow-xs font-medium ring-1 dark:bg-surface-100"
                      : "text-surface-600 hover:bg-surface-100 hover:text-surface-900",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 shrink-0",
                      isActive ? "text-primary-600" : "text-surface-400",
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.modified ? (
                    <span
                      className="bg-warning-500 h-1.5 w-1.5 shrink-0 rounded-full"
                      aria-label={tCommon("sectionModified")}
                    />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
